import { LEVELS, type LevelDef } from '../../../levels/levels';
import type { GeneratedPuzzle } from './generator';

// Tile constants
const Tile = {
  Floor: 0,
  Wall: 1,
  Target: 2,
  Box: 3,
  BoxOnTarget: 4,
  Player: 5,
  PlayerOnTarget: 6,
} as const;

export interface ParsedLevel {
  id: number;
  worldId: number;
  title: string;
  grid: number[][];
  width: number;
  height: number;
  optimalMoves: number;
  difficulty: number;
}

export const WORLDS = [
  { id: 1, name: 'Warehouse Basics', levelRange: [1, 20] as const, isPremium: false },
  { id: 2, name: 'Shipping Yard', levelRange: [21, 50] as const, isPremium: false },
  { id: 3, name: 'Factory Floor', levelRange: [51, 100] as const, isPremium: false },
];

function parseSokMap(sokString: string): { grid: number[][]; width: number; height: number } {
  const lines = sokString.split('\n').filter(l => l.length > 0);
  const width = Math.max(...lines.map(l => l.length));
  const grid: number[][] = [];

  for (const line of lines) {
    const row: number[] = [];
    for (let i = 0; i < width; i++) {
      const ch = i < line.length ? line[i] : ' ';
      switch (ch) {
        case '#': row.push(Tile.Wall); break;
        case '.': row.push(Tile.Target); break;
        case '$': row.push(Tile.Box); break;
        case '*': row.push(Tile.BoxOnTarget); break;
        case '@': row.push(Tile.Player); break;
        case '+': row.push(Tile.PlayerOnTarget); break;
        default: row.push(Tile.Floor); break;
      }
    }
    grid.push(row);
  }

  return { grid, width, height: grid.length };
}

// Parse all levels once on load
const parsedLevels: Map<number, ParsedLevel> = new Map();

for (const level of LEVELS) {
  const { grid, width, height } = parseSokMap(level.map);
  parsedLevels.set(level.id, {
    id: level.id,
    worldId: level.worldId,
    title: level.title,
    grid,
    width,
    height,
    optimalMoves: level.optimalMoves,
    difficulty: level.difficulty,
  });
}

export function getLevelById(id: number): ParsedLevel | undefined {
  return parsedLevels.get(id);
}

export function getLevelsForWorld(worldId: number): ParsedLevel[] {
  return LEVELS
    .filter(l => l.worldId === worldId)
    .map(l => parsedLevels.get(l.id)!)
    .filter(Boolean);
}

export const DAILY_GENERATED_ID = -1;

export function registerGeneratedDaily(puzzle: GeneratedPuzzle): number {
  parsedLevels.set(DAILY_GENERATED_ID, {
    id: DAILY_GENERATED_ID,
    worldId: 0,
    title: puzzle.title,
    grid: puzzle.grid,
    width: puzzle.width,
    height: puzzle.height,
    optimalMoves: puzzle.optimalMoves,
    difficulty: 1,
  });
  return DAILY_GENERATED_ID;
}

export function getTotalLevelCount(): number {
  return LEVELS.length;
}
